import Fuse from 'fuse';

class Faq extends HTMLElement {
  connectedCallback() {
    this.state = { search: '', poolSpa: [], chemical: [], problem: [], format: [] };
    this.items = Array.from(this.querySelectorAll('.faq__item'));
    this.totalCount = this.items.length;
    this.countEl = this.querySelector('.faq__count');
    this.emptyEl = this.querySelector('.faq__empty');
    this.searchInput = this.querySelector('.faq__search');
    this.filterBoxes = this.querySelectorAll('.faq__filter-box');
    this.sidebar = this.querySelector('.faq__sidebar');
    this.toggleBtn = this.querySelector('.faq__filter-toggle');
    this.closeBtn = this.querySelector('.faq__filter-close');
    this.backdrop = this.querySelector('.faq__backdrop');
    this.mainEl = this.querySelector('.faq__main');

    this.buildSearchIndex();

    this.bindCheckboxes();
    this.bindSearch();
    this.bindReset();
    this.bindDrawer();

    this.openHashTarget();
    this.onHashChange = () => this.openHashTarget();
    window.addEventListener('hashchange', this.onHashChange);
  }

  disconnectedCallback() {
    // Remove window/MediaQueryList listeners and document-level trapFocus listeners,
    // which (unlike listeners on elements inside this subtree) leak across the theme
    // editor's re-renders. Clear the scroll lock so removal mid-drawer doesn't stick.
    window.removeEventListener('hashchange', this.onHashChange);
    this.desktopQuery?.removeEventListener('change', this.onDesktopChange);
    document.body.style.overflow = '';
    removeTrapFocus();
  }

  bindDrawer() {
    if (!this.sidebar) return;
    this.toggleBtn?.addEventListener('click', () => this.openDrawer());
    this.closeBtn?.addEventListener('click', () => this.closeDrawer());
    this.backdrop?.addEventListener('click', () => this.closeDrawer());
    // Tab cycling is handled by the shared trapFocus (see openDrawer); only Escape here.
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.sidebar.classList.contains('is-open')) this.closeDrawer();
    });

    // Reset drawer state when the sidebar becomes static at the desktop breakpoint,
    // so an open drawer doesn't leave a stuck backdrop or body scroll lock.
    this.desktopQuery = window.matchMedia('(min-width: 1024px)');
    this.onDesktopChange = (event) => {
      if (event.matches) this.closeDrawer({ focus: false });
    };
    this.desktopQuery.addEventListener('change', this.onDesktopChange);
  }

  openDrawer() {
    if (!this.sidebar) return;
    this.sidebar.classList.add('is-open');
    // Dialog semantics apply only to the mobile drawer (stripped on close), so the
    // desktop static sidebar isn't announced as a modal.
    this.sidebar.setAttribute('role', 'dialog');
    this.sidebar.setAttribute('aria-modal', 'true');
    if (this.backdrop) this.backdrop.hidden = false;
    if (this.mainEl) this.mainEl.inert = true;
    this.toggleBtn?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    // Shared focus trap (assets/global.js): installs cycling listeners, focuses close.
    trapFocus(this.sidebar, this.closeBtn);
  }

  closeDrawer({ focus = true } = {}) {
    if (!this.sidebar) return;
    this.sidebar.classList.remove('is-open');
    this.sidebar.removeAttribute('role');
    this.sidebar.removeAttribute('aria-modal');
    if (this.backdrop) this.backdrop.hidden = true;
    if (this.mainEl) this.mainEl.inert = false;
    // Removing .is-open applies visibility:hidden (mobile), blurring any focused
    // control inside the drawer; removeTrapFocus then returns focus to the toggle.
    this.toggleBtn?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    // Tear down the focus trap, returning focus to the toggle (the desktop-breakpoint
    // handler passes focus:false to skip this).
    removeTrapFocus(focus ? this.toggleBtn : null);
  }

  buildSearchIndex() {
    this.searchRecords = this.items.map((el, index) => {
      const question = el.querySelector('.faq__item-question')?.textContent || '';
      const answer = el.querySelector('.faq__item-answer')?.textContent || '';
      return { index, question, answer };
    });

    this.fuse = new Fuse(this.searchRecords, {
      keys: [
        { name: 'question', weight: 2 },
        { name: 'answer', weight: 1 },
      ],
      threshold: 0.3,
      ignoreLocation: true,
      minMatchCharLength: 2,
    });
  }

  bindCheckboxes() {
    this.filterBoxes.forEach((box) => {
      const group = box.dataset.filterGroup;
      // data-filter-group must match a state key; otherwise the group writes to an
      // ignored key and silently does nothing, so skip and flag it.
      if (!group || !(group in this.state)) {
        console.warn('[faq] filter box has unknown data-filter-group:', group, box);
        return;
      }
      const inputs = box.querySelectorAll('.faq__check-input');
      inputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.state[group] = Array.from(inputs)
            .filter((i) => i.checked)
            .map((i) => i.dataset.value);
          this.applyFilter();
        });
      });
    });
  }

  bindSearch() {
    if (!this.searchInput) return;
    const handler = debounce((event) => {
      this.state.search = event.target.value;
      this.applyFilter();
    }, 150);
    this.searchInput.addEventListener('input', handler);
  }

  bindReset() {
    const reset = () => this.reset();
    this.querySelectorAll('.faq__reset, .faq__empty-reset').forEach((btn) => {
      btn.addEventListener('click', reset);
    });
  }

  openHashTarget() {
    if (!location.hash || !location.hash.startsWith('#faq-')) return;
    const target = document.getElementById(location.hash.slice(1));
    if (!target || target.tagName !== 'DETAILS') return;
    target.setAttribute('open', '');
    requestAnimationFrame(() => target.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  }

  matchPoolSpa(el) {
    const selected = this.state.poolSpa;
    if (selected.length === 0) return true;
    return (
      (selected.includes('Pool') && el.dataset.pool === 'true') ||
      (selected.includes('Spa') && el.dataset.spa === 'true')
    );
  }

  matchListFacet(el, key) {
    const selected = this.state[key];
    if (selected.length === 0) return true;
    const values = (el.dataset[key] || '').split('|');
    return selected.some((v) => values.includes(v));
  }

  computeSearchMatches() {
    const q = this.state.search.trim();
    if (!q) return null;
    return new Set(this.fuse.search(q).map((r) => r.item.index));
  }

  applyFilter() {
    const searchMatches = this.computeSearchMatches();
    let visible = 0;
    this.items.forEach((el, index) => {
      const show =
        this.matchPoolSpa(el) &&
        this.matchListFacet(el, 'chemical') &&
        this.matchListFacet(el, 'problem') &&
        this.matchListFacet(el, 'format') &&
        (searchMatches === null || searchMatches.has(index));

      const wrapper = el.parentElement;
      if (wrapper) wrapper.style.display = show ? '' : 'none';
      el.removeAttribute('open');
      if (show) visible++;
    });

    if (this.countEl) {
      this.countEl.textContent = `(${visible})`;
    }
    if (this.emptyEl) {
      this.emptyEl.toggleAttribute('hidden', visible !== 0);
    }
  }

  reset() {
    this.state = { search: '', poolSpa: [], chemical: [], problem: [], format: [] };
    if (this.searchInput) this.searchInput.value = '';
    this.querySelectorAll('.faq__check-input').forEach((input) => {
      input.checked = false;
    });
    this.applyFilter();
  }
}

customElements.define('faq-section', Faq);
